import { createSelector } from '@reduxjs/toolkit';
import { isEqual, orderBy } from 'lodash-es';
import { selectSelectedProjectId } from 'store/projects/selectors/projectsSelectors';
import { getFirestopImageTypeOrder } from '../constants/firestopImageTypes';

const selectFirestopDocumentationState = (state) => state.firestopDocumentation;

export const selectFirestopListsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopLists.entities
);

export const selectFirestopItemsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopItems.entities
);

export const selectFirestopImagesEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopImages.entities
);

export const selectFirestopDetailsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopDetails.entities
);

export const selectFirestopDetailsArray = createSelector(
  [selectFirestopDetailsEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectFirestopDetails = createSelector(
  [selectFirestopDetailsArray, selectSelectedProjectId],
  (firestopDetails, selectedProjectId) =>
    firestopDetails.filter(
      (firestopDetail) =>
        firestopDetail.projectId === selectedProjectId &&
        !firestopDetail.deletedAt
    )
);

export const selectFirestopProductsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopProducts.entities
);

export const selectFirestopProductsArray = createSelector(
  [selectFirestopProductsEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectFirestopProducts = createSelector(
  [selectFirestopProductsArray, selectSelectedProjectId],
  (firestopProducts, selectedProjectId) =>
    firestopProducts.filter(
      (firestopProduct) =>
        firestopProduct.projectId === selectedProjectId &&
        !firestopProduct.deletedAt
    )
);

export const selectFirestopDetailImagesEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopDetailImages.entities
);

export const selectFirestopLabelsEntities = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopLabels.entities
);

export const selectFirestopLabelsArray = createSelector(
  [selectFirestopLabelsEntities],
  (entities) => Object.values(entities)
);

export const selectFirestopLabelsByFirestopItemId = createSelector(
  [selectFirestopLabelsArray, (_, firestopItemId) => firestopItemId],
  (firestopLabels, firestopItemId) =>
    orderBy(
      firestopLabels.filter(
        (firestopLabel) =>
          firestopLabel.firestopItemId === firestopItemId &&
          !firestopLabel.deletedAt
      ),
      ['createdAt'],
      ['asc']
    )
);

export const selectFirestopLabelsByListId = createSelector(
  [selectFirestopLabelsArray, (_, listId) => listId],
  (firestopLabels, listId) =>
    orderBy(
      firestopLabels.filter(
        (firestopLabel) =>
          firestopLabel.listId === listId && !firestopLabel.deletedAt
      ),
      ['createdAt'],
      ['asc']
    )
);

export const selectLabelDialogFirestopItemId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.labelDialogFirestopItemId
);

export const selectLabelDialogListId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.labelDialogListId
);

export const selectIsFirestopLabelDialogOpen = createSelector(
  [selectLabelDialogFirestopItemId, selectLabelDialogListId],
  (labelDialogFirestopItemId, labelDialogListId) =>
    Boolean(labelDialogFirestopItemId) || Boolean(labelDialogListId)
);

export const selectIsCreatingFirestopLabels = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isCreatingFirestopLabels
);

export const selectFirestopDetailImagesArray = createSelector(
  [selectFirestopDetailImagesEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectFirestopDetailImages = createSelector(
  [selectFirestopDetailImagesArray, selectSelectedProjectId],
  (firestopDetailImages, selectedProjectId) =>
    firestopDetailImages.filter(
      (firestopDetailImage) =>
        firestopDetailImage.projectId === selectedProjectId &&
        !firestopDetailImage.deletedAt
    )
);

export const selectFirestopDetailImageByDetailId = createSelector(
  [selectSelectedProjectFirestopDetailImages, (_, detailId) => detailId],
  (firestopDetailImages, detailId) => {
    const matchingImages = firestopDetailImages.filter(
      (firestopDetailImage) => firestopDetailImage.detailId === detailId
    );

    return orderBy(matchingImages, ['createdAt'], ['desc'])[0];
  }
);

export const selectFirestopImagesArray = createSelector(
  [selectFirestopImagesEntities],
  (entities) => Object.values(entities)
);

export const selectFirestopListsArray = createSelector(
  [selectFirestopListsEntities],
  (entities) => Object.values(entities)
);

export const selectFirestopItemsArray = createSelector(
  [selectFirestopItemsEntities],
  (entities) => Object.values(entities)
);

export const selectSelectedProjectFirestopLists = createSelector(
  [selectFirestopListsArray, selectSelectedProjectId],
  (firestopLists, selectedProjectId) =>
    firestopLists.filter(
      (firestopList) =>
        firestopList.projectId === selectedProjectId && !firestopList.deletedAt
    )
);

export const selectNotDeletedFirestopItemsArray = createSelector(
  [selectFirestopItemsArray],
  (firestopItems) =>
    firestopItems.filter((firestopItem) => !firestopItem.deletedAt)
);

export const selectSelectedProjectFirestopItems = createSelector(
  [selectNotDeletedFirestopItemsArray, selectSelectedProjectId],
  (firestopItems, selectedProjectId) =>
    firestopItems.filter(
      (firestopItem) => firestopItem.projectId === selectedProjectId
    )
);

export const selectFirestopItemsToCreate = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopItemsToCreate
);

export const selectFirestopImagesToAdd = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopImagesToAdd
);

export const selectIsFirestopDocumentationDataLoading = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.loading
);

export const selectIsCreateFirestopListDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isCreateFirestopListDialogOpen
);

export const selectSelectedFirestopListId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopListId
);

export const selectIsEditingListAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isEditingListAttributes
);

export const selectIsSavingFirestopListAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isSavingListAttributes
);

export const selectFirestopListAttributeModifications = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopListAttributeModifications
);

export const selectIsAddNewFirestopDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isAddNewFirestopDialogOpen
);

export const selectIsCreatingOrUpdatingFirestopData = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isCreatingOrUpdatingFirestopData
);

export const selectSelectedFirestopItemIdToDelete = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopItemIdToDelete
);

export const selectSelectedFirestopItemIdToEdit = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedFirestopItemIdToEdit
);

export const selectEditedFirestopAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.editedFirestopAttributes
);

export const selectFirestopEditImagesToDelete = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopEditImagesToDelete
);

export const selectFirestopLabelsToDelete = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopLabelsToDelete
);

export const selectSelectedAdditionFirestopItemId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.selectedAdditionFirestopItemId
);

export const selectFirestopAdditionAttributes = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopAdditionAttributes
);

export const selectFirestopImageDescriptions = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.imageDescriptions
);

export const selectLatestWorkedAtByResource = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.latestWorkedAtByResource
);

export const selectIsPrintingFirestopListPdf = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isPrintingFirestopListPdf
);

export const selectIsFirestopLocationsOverviewDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isFirestopLocationsOverviewDialogOpen
);

export const selectIsFirestopCatalogsDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isFirestopCatalogsDialogOpen
);

export const selectHighlightedFirestopItemId = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.highlightedFirestopItemId
);

export const selectIsConfirmDeleteFirestopListDialogOpen = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isConfirmDeleteFirestopListDialogOpen
);

export const selectIsDeletingFirestopList = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.isDeletingFirestopList
);

export const selectSelectedFirestopList = createSelector(
  [selectFirestopListsEntities, selectSelectedFirestopListId],
  (firestopListEntities, selectedFirestopListId) =>
    firestopListEntities[selectedFirestopListId]
);

const selectSelectedFirestopListFirestopItems = createSelector(
  [selectFirestopItemsArray, selectSelectedFirestopListId],
  (firestopItems, selectedFirestopListId) =>
    firestopItems.filter(
      (firestopItem) => firestopItem.listId === selectedFirestopListId
    )
);

export const selectSelectedFirestopListWithModifications = createSelector(
  [selectSelectedFirestopList, selectFirestopListAttributeModifications],
  (firestopList, firestopListModifications) => ({
    ...firestopList,
    ...firestopListModifications,
  })
);

export const selectIsFirestopListDirty = createSelector(
  [selectSelectedFirestopList, selectFirestopListAttributeModifications],
  (firestopList, firestopListModifications) =>
    Object.keys(firestopListModifications).some((attributeKey) => {
      const persistedValue = firestopList
        ? firestopList[attributeKey]
        : undefined;
      const modifiedValue = firestopListModifications[attributeKey];

      const isPersistedEmpty =
        persistedValue === undefined ||
        persistedValue === null ||
        persistedValue === '';
      const isModifiedEmpty =
        modifiedValue === undefined ||
        modifiedValue === null ||
        modifiedValue === '';

      if (isPersistedEmpty && isModifiedEmpty) {
        return false;
      }

      return !isEqual(persistedValue, modifiedValue);
    })
);

export const selectSelectedFirestopListData = createSelector(
  [selectSelectedFirestopListFirestopItems],
  (listFirestopItems) => {
    const inProgressFirestopItems = [];
    const completedFirestopItems = [];

    listFirestopItems.forEach((firestopItem) => {
      if (firestopItem.status === 'COMPLETED') {
        completedFirestopItems.push(firestopItem);
      } else {
        inProgressFirestopItems.push(firestopItem);
      }
    });

    const createdAtSortedInProgressFirestopItems = orderBy(
      inProgressFirestopItems,
      'createdAt',
      'desc'
    );

    const createdAtSortedCompletedFirestopItems = orderBy(
      completedFirestopItems,
      'createdAt',
      'desc'
    );

    const sortedFirestopItems = [
      ...createdAtSortedInProgressFirestopItems,
      ...createdAtSortedCompletedFirestopItems,
    ];

    return {
      firestopItems: sortedFirestopItems,
    };
  }
);

export const selectFirestopSummaryData = createSelector(
  [selectSelectedProjectFirestopLists, selectSelectedProjectFirestopItems],
  (firestopLists, firestopItems) => {
    const firestopListsAmount = firestopLists.length;
    const firestopItemsAmount = firestopItems.length;

    const completedFirestopItems = [];
    const inProgressFirestopItems = [];

    firestopItems.forEach((firestopItem) => {
      if (firestopItem.status === 'COMPLETED') {
        completedFirestopItems.push(firestopItem);
      } else {
        inProgressFirestopItems.push(firestopItem);
      }
    });

    const totalCompletedFirestops = completedFirestopItems.length;
    const totalInProgressFirestops = inProgressFirestopItems.length;

    return {
      firestopListsAmount,
      firestopItemsAmount,
      totalCompletedFirestops,
      totalInProgressFirestops,
    };
  }
);

export const selectSelectedListFirestopLocationMarkers = createSelector(
  [selectSelectedFirestopListData, selectSelectedFirestopList],
  (firestopListData, firestopList) => {
    const listFloorplanId = firestopList?.floorplanId;

    if (!listFloorplanId) {
      return [];
    }

    return firestopListData.firestopItems
      .map((firestopItem, index) => ({
        firestopItem,
        firestopNumber: index + 1,
      }))
      .filter(
        ({ firestopItem }) =>
          firestopItem.floorplanId === listFloorplanId &&
          firestopItem.locationCoordinates
      )
      .map(({ firestopItem, firestopNumber }) => ({
        number: firestopNumber,
        firestopItemId: firestopItem.id,
        x: firestopItem.locationCoordinates.x,
        y: firestopItem.locationCoordinates.y,
      }));
  }
);

export const selectSelectedFirestopItemToEdit = createSelector(
  [selectFirestopItemsEntities, selectSelectedFirestopItemIdToEdit],
  (firestopItems, selectedFirestopItemId) =>
    firestopItems[selectedFirestopItemId]
);

export const selectSelectedProjectFirestopImages = createSelector(
  [selectFirestopImagesArray, selectSelectedProjectId],
  (firestopImages, selectedProjectId) =>
    firestopImages.filter(
      (firestopImage) => firestopImage.projectId === selectedProjectId
    )
);

export const selectFirestopImagesByFirestopItemId = createSelector(
  [selectSelectedProjectFirestopImages, (_, args) => args],
  (firestopImages, args) => {
    const thisFirestopImages = firestopImages.filter(
      (firestopImage) => firestopImage.firestopItemId === args
    );

    // Blueprint/location images come first (ordered by their image type), then
    // the actual firestop photos. Within each group the original
    // capture/add order is kept via createdAt.
    const sortedImages = orderBy(
      thisFirestopImages,
      [
        (firestopImage) => getFirestopImageTypeOrder(firestopImage),
        'createdAt',
      ],
      ['asc', 'asc']
    );

    return sortedImages;
  }
);

export const selectSelectedFirestopItemImages = createSelector(
  [
    selectFirestopImagesArray,
    selectSelectedFirestopItemIdToEdit,
    selectFirestopEditImagesToDelete,
  ],
  (firestopImages, selectedFirestopItemId, imagesAddedForDeletion) =>
    firestopImages.filter(
      (firestopImage) =>
        firestopImage.firestopItemId === selectedFirestopItemId &&
        !imagesAddedForDeletion.includes(firestopImage.id)
    )
);

export const selectSelectedFirestopItemLabels = createSelector(
  [
    selectFirestopLabelsArray,
    selectSelectedFirestopItemIdToEdit,
    selectFirestopLabelsToDelete,
  ],
  (firestopLabels, selectedFirestopItemId, labelsStagedForDeletion) =>
    firestopLabels.filter(
      (firestopLabel) =>
        firestopLabel.firestopItemId === selectedFirestopItemId &&
        !firestopLabel.deletedAt &&
        !labelsStagedForDeletion.includes(firestopLabel.id)
    )
);

export const selectFirestopListsSearchFilterValue = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopListsSearchFilterValue
);

const buildFirestopItemSearchTexts = (firestopItem, firestopDetailEntities) => {
  const matchingFirestopDetail = firestopDetailEntities[firestopItem.detailId];
  const detailName = matchingFirestopDetail ? matchingFirestopDetail.name : '';

  const productNames = (firestopItem.products || []).map(
    (product) => product.name
  );

  return [
    firestopItem.location,
    firestopItem.apartmentNumber,
    firestopItem.penetrationsCount != null
      ? String(firestopItem.penetrationsCount)
      : '',
    firestopItem.status === 'COMPLETED' ? 'asennettu' : 'kesken',
    firestopItem.description,
    detailName,
    ...productNames,
  ];
};

const buildFirestopListSearchText = (
  firestopList,
  listFirestopItems,
  firestopDetailEntities
) => {
  const listAttributeTexts = [firestopList.name, firestopList.targetName];

  const itemAttributeTexts = listFirestopItems.flatMap((firestopItem) =>
    buildFirestopItemSearchTexts(firestopItem, firestopDetailEntities)
  );

  return [...listAttributeTexts, ...itemAttributeTexts]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

export const selectSearchFilteredProjectFirestopLists = createSelector(
  [
    selectSelectedProjectFirestopLists,
    selectNotDeletedFirestopItemsArray,
    selectFirestopDetailsEntities,
    selectFirestopListsSearchFilterValue,
  ],
  (firestopLists, firestopItems, firestopDetailEntities, searchFilterValue) => {
    const normalizedSearchFilterValue = searchFilterValue.trim().toLowerCase();

    if (!normalizedSearchFilterValue) {
      return firestopLists;
    }

    return firestopLists.filter((firestopList) => {
      const listFirestopItems = firestopItems.filter(
        (firestopItem) => firestopItem.listId === firestopList.id
      );

      const searchableText = buildFirestopListSearchText(
        firestopList,
        listFirestopItems,
        firestopDetailEntities
      );

      return searchableText.includes(normalizedSearchFilterValue);
    });
  }
);

export const selectFirestopItemsSearchFilterValue = createSelector(
  [selectFirestopDocumentationState],
  (state) => state.firestopItemsSearchFilterValue
);

export const selectHasSelectedFirestopListItems = createSelector(
  [selectSelectedFirestopListData],
  (firestopListData) => firestopListData.firestopItems.length > 0
);

export const selectSearchFilteredSelectedFirestopListItems = createSelector(
  [
    selectSelectedFirestopListData,
    selectFirestopDetailsEntities,
    selectFirestopItemsSearchFilterValue,
  ],
  (firestopListData, firestopDetailEntities, searchFilterValue) => {
    const numberedFirestopItems = firestopListData.firestopItems.map(
      (firestopItem, index) => ({
        firestopItem,
        firestopNumber: index + 1,
      })
    );

    const normalizedSearchFilterValue = searchFilterValue.trim().toLowerCase();

    if (!normalizedSearchFilterValue) {
      return numberedFirestopItems;
    }

    return numberedFirestopItems.filter(({ firestopItem }) => {
      const searchableText = buildFirestopItemSearchTexts(
        firestopItem,
        firestopDetailEntities
      )
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedSearchFilterValue);
    });
  }
);

export const selectFirestopItemsCountTotalsByFirestopListId = createSelector(
  [selectSelectedProjectFirestopItems, (_, args) => args],
  (firestopItems, firestopListId) => {
    const listFirestopItems = firestopItems.filter(
      (firestopItem) => firestopItem.listId === firestopListId
    );

    const completedFirestopItems = [];
    const inProgressFirestopItems = [];

    listFirestopItems.forEach((firestopItem) => {
      if (firestopItem.status === 'COMPLETED') {
        completedFirestopItems.push(firestopItem);
      } else {
        inProgressFirestopItems.push(firestopItem);
      }
    });

    const firestopItemsAmount = listFirestopItems.length;
    const totalCompletedFirestops = completedFirestopItems.length;
    const totalInProgressFirestops = inProgressFirestopItems.length;

    return {
      firestopItemsAmount,
      totalCompletedFirestops,
      totalInProgressFirestops,
    };
  }
);
